import { useEffect, useRef, useState } from 'react'
import { createFileRoute, Link, useRouter } from '@tanstack/react-router'
import { z } from 'zod'
import { AlertTriangleIcon, CheckCircle2Icon, CheckIcon, CircleIcon, InfoIcon, OctagonAlertIcon, Undo2Icon } from 'lucide-react'
import {
  FIRST_YEAR_INCLUDED,
  renewalSentence,
  type AuditFlag,
  type AuditRisk,
  type AuditView,
  type PreviewView,
  type RequestPreviewRequest,
  type RequestPreviewResponse,
} from '@hedgehog/shared'
import { api, formatPrice } from '@/lib/api'
import { AppShell } from '@/components/app/app-shell'
import { formatDate } from '@/components/app/format'
import { PayButton } from '@/components/app/pay-panel'
import { ScreenFrame } from '@/components/app/screens'
import { SpeedScore } from '@/components/app/speed-score'
import { ErrorState, FormError, LoadingState, errorMessage, errorStatus } from '@/components/app/states'
import { WaitlistForm } from '@/components/app/waitlist-form'
import { buttonVariants } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { useInterval } from '@/hooks/use-interval'
import { usePublicConfig } from '@/components/marketing/config'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/audit/$auditId')({
  // k: the key in the emailed link. It starts the homepage preview and shows it on this page.
  validateSearch: z.object({ k: z.string().optional().catch(undefined) }),
  loaderDeps: ({ search }) => ({ k: search.k }),
  loader: ({ params, deps, abortController }) =>
    api<AuditView>(`/audits/${encodeURIComponent(params.auditId)}${deps.k ? `?k=${encodeURIComponent(deps.k)}` : ''}`, {
      signal: abortController.signal,
    }),
  head: ({ loaderData }) => ({
    meta: [{ title: loaderData ? `Audit for ${loaderData.domain} · Hedgehog` : 'Site audit · Hedgehog' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  pendingComponent: () => (
    <AppShell>
      <LoadingState label="Loading your audit" />
    </AppShell>
  ),
  errorComponent: ({ error }) => (
    <AppShell>
      {errorStatus(error) === 404 ? (
        <NotFound />
      ) : (
        <ErrorState error={error} title="We could not load this audit" />
      )}
    </AppShell>
  ),
  component: AuditPage,
})

function NotFound() {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-3 py-16 text-center">
      <h1 className="text-2xl font-semibold">This audit is no longer available</h1>
      <p className="text-muted-foreground">Audits are kept for 30 days. Run a new one, it takes about three minutes.</p>
      <Link to="/" className={buttonVariants({ size: 'lg' })}>
        Audit my site
      </Link>
    </div>
  )
}

function AuditPage() {
  const audit = Route.useLoaderData()
  const router = useRouter()
  useInterval(
    () => void router.invalidate({ filter: (m) => m.routeId === Route.id }),
    audit.status === 'running' ? 2000 : null,
  )

  return (
    <AppShell>
      <div className="flex flex-col gap-8">
        <header className="flex flex-col gap-1">
          <p className="text-sm text-muted-foreground">Site audit</p>
          <h1 className="text-3xl font-semibold tracking-tight text-balance wrap-anywhere">{audit.businessName}</h1>
          <a href={audit.url} target="_blank" rel="noopener noreferrer" className="w-fit text-muted-foreground underline-offset-4 hover:underline">
            {audit.domain}
          </a>
        </header>
        {audit.status === 'running' && <Progress audit={audit} />}
        {audit.status === 'failed' && <Failed audit={audit} />}
        {audit.status === 'done' && <Results audit={audit} />}
      </div>
    </AppShell>
  )
}

const STAGES: Array<{ key: AuditView['progress']['stage']; label: string }> = [
  { key: 'checking', label: 'Checking your site answers' },
  { key: 'crawling', label: 'Finding your pages' },
  { key: 'analysing', label: 'Grouping pages and checking features' },
  { key: 'speed', label: 'Testing mobile speed' },
]

function Progress({ audit }: { audit: AuditView }) {
  const current = STAGES.findIndex((s) => s.key === audit.progress.stage)
  return (
    <Card>
      <CardHeader>
        <CardTitle>Auditing your site</CardTitle>
        <CardDescription>This takes up to three minutes. You can keep this page open; it updates by itself.</CardDescription>
      </CardHeader>
      <CardContent>
        <ol className="flex flex-col gap-3" aria-live="polite">
          {STAGES.map((s, i) => {
            const state = audit.progress.stage === 'done' || i < current ? 'done' : i === current ? 'active' : 'todo'
            return (
              <li key={s.key} className="flex items-center gap-3">
                {state === 'done' ? (
                  <CheckCircle2Icon className="size-5 text-success" aria-hidden />
                ) : state === 'active' ? (
                  <Spinner className="size-5" aria-hidden />
                ) : (
                  <CircleIcon className="size-5 text-muted-foreground" aria-hidden />
                )}
                <span className={cn(state === 'todo' && 'text-muted-foreground')}>
                  {s.label}
                  {s.key === 'crawling' && audit.progress.pagesCrawled > 0 && (
                    <span className="text-muted-foreground"> ({audit.progress.pagesCrawled} found so far)</span>
                  )}
                  <span className="sr-only">
                    {state === 'done' ? ', done' : state === 'active' ? ', in progress' : ', waiting'}
                  </span>
                </span>
              </li>
            )
          })}
        </ol>
      </CardContent>
    </Card>
  )
}

function Failed({ audit }: { audit: AuditView }) {
  return (
    <div role="alert" className="flex flex-col items-start gap-3 rounded-xl border border-border p-6">
      <h2 className="text-lg font-semibold">We could not finish the audit</h2>
      <p className="text-muted-foreground">
        {audit.declineReason ?? 'Your site stopped answering while we checked it. This is often temporary.'}
      </p>
      <Link to="/" className={buttonVariants({ variant: 'outline', size: 'lg' })}>
        Try again
      </Link>
    </div>
  )
}

const FLAG_LABELS: Record<AuditFlag['feature'], string> = {
  shop: 'Online shop',
  booking: 'Booking system',
  membership: 'Members area',
  multilingual: 'Several languages',
  forum: 'Forum',
  login: 'Pages behind a login',
  too_many_pages: 'More than 100 pages',
  script_rendered: 'Content drawn by scripts',
  bot_protection: 'Bot protection blocks our crawler',
}

function Results({ audit }: { audit: AuditView }) {
  const builder = audit.builder
    ? `${audit.builder}${audit.builderVersion ? ` ${audit.builderVersion}` : ''}`
    : audit.profile === 'classic'
      ? 'Classic theme'
      : 'Not detected'

  return (
    <>
      <section aria-labelledby="facts" className="flex flex-col gap-4">
        <h2 id="facts" className="sr-only">
          What we found
        </h2>
        <dl className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Fact label="Pages found" value={audit.pagesFound === null ? 'Unknown' : String(audit.pagesFound)} />
          <Fact label="Page types" value={String(audit.pageTypes.length)} />
          <Fact label="Built with" value={builder} />
          <Fact label="WordPress version" value={audit.wpVersion ?? 'Hidden'} />
        </dl>
        <div className="rounded-xl border border-border p-4">
          <SpeedScore score={audit.speed?.score ?? null} caption="Mobile speed score today" />
          {audit.speed?.lcpMs != null && (
            <p className="mt-2 text-sm text-muted-foreground">
              The main content appears after {(audit.speed.lcpMs / 1000).toFixed(1)} seconds on a phone.
            </p>
          )}
        </div>
      </section>

      {audit.pageTypes.length > 0 && (
        <section aria-labelledby="types" className="flex flex-col gap-3">
          <h2 id="types" className="text-xl font-semibold">
            Page types
          </h2>
          <p className="text-sm text-muted-foreground">Pages that share a layout. We rebuild each layout once and fill it with every page's content.</p>
          <ul className="divide-y divide-border rounded-xl border border-border">
            {audit.pageTypes.map((t) => (
              <li key={t.name} className="flex items-center justify-between gap-4 px-4 py-3">
                <a href={t.sampleUrl} target="_blank" rel="noopener noreferrer" className="min-w-0 truncate underline-offset-4 hover:underline">
                  {t.name}
                </a>
                <span className="shrink-0 text-sm text-muted-foreground tabular-nums">
                  {t.count} {t.count === 1 ? 'page' : 'pages'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {audit.risks.length > 0 && (
        <section aria-labelledby="risks" className="flex flex-col gap-3">
          <h2 id="risks" className="text-xl font-semibold">
            Things we noticed
          </h2>
          <ul className="flex flex-col gap-2">
            {audit.risks.map((r) => (
              <Risk key={r.title} risk={r} />
            ))}
          </ul>
        </section>
      )}

      {audit.inScope === false ? <Declined audit={audit} /> : <Offer audit={audit} />}

      <p className="text-sm text-muted-foreground">This audit stays available at this address until {formatDate(audit.expiresAt)}.</p>
    </>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-border p-4">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-xl font-semibold break-words">{value}</dd>
    </div>
  )
}

function Risk({ risk }: { risk: AuditRisk }) {
  const Icon = risk.level === 'critical' ? OctagonAlertIcon : risk.level === 'warning' ? AlertTriangleIcon : InfoIcon
  const color = risk.level === 'critical' ? 'text-destructive' : risk.level === 'warning' ? 'text-warning' : 'text-muted-foreground'
  const word = risk.level === 'critical' ? 'Serious' : risk.level === 'warning' ? 'Warning' : 'Note'
  return (
    <li className="flex gap-3 rounded-xl border border-border p-4">
      <Icon className={cn('mt-0.5 size-5 shrink-0', color)} aria-hidden />
      <div className="flex flex-col gap-1">
        <p className="font-medium">
          <span className="sr-only">{word}: </span>
          {risk.title}
        </p>
        <p className="text-sm text-muted-foreground">{risk.detail}</p>
        {risk.items && risk.items.length > 0 && (
          <ul className="mt-1 flex flex-wrap gap-1.5" aria-label="Found">
            {risk.items.map((item) => (
              <li key={item} className="rounded-md border border-border bg-muted px-2 py-0.5 font-mono text-xs">
                {item}
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  )
}

function Declined({ audit }: { audit: AuditView }) {
  const reason = audit.declineReason ?? 'Your site uses features we cannot rebuild yet.'
  return (
    <section aria-labelledby="declined" className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6">
      <h2 id="declined" className="text-xl font-semibold">
        We cannot rebuild this site yet
      </h2>
      <p>{reason}</p>
      {audit.outOfScope.length > 0 && (
        <ul className="flex flex-col gap-2 text-sm">
          {audit.outOfScope.map((f) => (
            <li key={f.feature}>
              <span className="font-medium">{FLAG_LABELS[f.feature]}</span>
              <span className="text-muted-foreground"> · {f.evidence}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="text-muted-foreground">Leave your email and we will tell you when we support sites like yours. Nothing else.</p>
      <WaitlistForm url={audit.url} reason={audit.outOfScope.map((f) => f.feature).join(',') || reason} />
    </section>
  )
}

/** What every rebuild includes; true for every site we take. */
const INCLUDED = [
  'Your design, kept as it is, on every page',
  'Every old address redirects to its new page, with its title and description',
  'A simple editor for your text and photos',
  'No WordPress, plugins or updates to look after',
]

/**
 * The selling panel: what this rebuild removes (from this audit), what it includes, the price and
 * the buy button, then the free homepage preview as it builds.
 */
function Offer({ audit }: { audit: AuditView }) {
  const { k } = Route.useSearch()
  const [preview, setPreview] = useState<PreviewView | null>(null)
  const fixed = audit.risks.filter((r) => r.fixed && r.level !== 'info')
  const price = formatPrice(audit.priceCents, audit.currency)

  return (
    <section aria-labelledby="offer" className="overflow-hidden rounded-2xl border border-primary">
      <div className="grid gap-8 bg-primary p-6 text-primary-foreground sm:p-8 lg:grid-cols-[1.3fr_1fr] lg:gap-10">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <h2 id="offer" className="text-3xl font-semibold tracking-tight text-balance sm:text-4xl">
              {fixed.length > 0
                ? `Fix ${fixed.length === 1 ? 'this problem' : `these ${fixed.length} problems`} for good`
                : `Rebuild ${audit.domain} on a fast, secure platform`}
            </h2>
            <p className="text-lg text-primary-foreground/80">
              We rebuild {audit.domain} with the same design. You keep your content and your address, and every old page address keeps working.
            </p>
          </div>
          {fixed.length > 0 && (
            <ul className="flex flex-col gap-2" aria-label="Problems the rebuild removes">
              {fixed.map((r) => (
                <li key={r.title} className="flex items-start gap-3 rounded-lg bg-primary-foreground/10 px-4 py-3">
                  <CheckIcon className="mt-0.5 size-5 shrink-0 text-[var(--color-signal)]" aria-hidden />
                  <span>
                    <span className="line-through decoration-primary-foreground/50">{r.title}</span>
                    <span className="block text-sm text-primary-foreground/75">Gone on the new site.</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <ul className="flex flex-col gap-2.5" aria-label="Included">
            {INCLUDED.map((line) => (
              <li key={line} className="flex items-start gap-3">
                <CheckIcon className="mt-0.5 size-5 shrink-0 text-[var(--color-signal)]" aria-hidden />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col gap-5 self-start rounded-xl bg-card p-6 text-card-foreground shadow-lg">
          <div className="flex flex-col gap-1">
            <p className="flex items-baseline gap-2">
              <span className="text-5xl font-semibold tracking-tight">{price}</span>
              <span className="text-muted-foreground">once</span>
            </p>
            <p className="text-sm text-muted-foreground">
              {audit.pagesFound ? `For all ${audit.pagesFound} pages. ` : ''}
              {FIRST_YEAR_INCLUDED} {renewalSentence(audit.renewalYearlyCents, audit.currency)}
            </p>
          </div>
          <BuyAction audit={audit} k={k} preview={preview} />
          <p className="flex items-start gap-2 border-t border-border pt-4 text-sm text-muted-foreground">
            <Undo2Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
            Two rounds of changes are included. Not happy after that? We refund you in full.
          </p>
        </div>
      </div>

      <div className="bg-card p-6 text-card-foreground sm:p-8">
        <PreviewArea audit={audit} k={k} onPreview={setPreview} />
      </div>
    </section>
  )
}

const buyClass = 'h-13 w-full rounded-lg bg-[var(--color-signal)] text-lg font-bold text-[var(--color-signal-ink)] hover:bg-[var(--color-signal)]/90'

function BuyAction({ audit, k, preview }: { audit: AuditView; k: string | undefined; preview: PreviewView | null }) {
  const { config } = usePublicConfig()
  if (preview && (preview.state === 'preview_requested' || preview.state === 'preview_ready')) {
    return <PayButton preview={preview} className={buyClass} label={preview.paymentsEnabled ? `Rebuild my site for ${formatPrice(preview.priceCents, preview.currency)}` : undefined} />
  }
  if (preview) {
    return (
      <Link to="/o/$orderId" params={{ orderId: preview.orderId }} className={cn(buttonVariants({ size: 'lg' }), 'w-full')}>
        Go to my order
      </Link>
    )
  }
  if (k && audit.hasEmail) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        The order button appears once your free preview has started below.
      </p>
    )
  }
  return (
    <p className="text-sm text-muted-foreground">
      {audit.hasEmail
        ? 'Open this report from the link in our email to start your free preview and order.'
        : `To start your free preview, write to ${config?.supportEmail ?? 'us'} with your website address.`}
    </p>
  )
}

/** Starts the preview from the emailed link (after the terms, for outreach audits) and shows it. */
function PreviewArea({ audit, k, onPreview }: { audit: AuditView; k: string | undefined; onPreview: (p: PreviewView) => void }) {
  const router = useRouter()
  const [accepted, setAccepted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const started = useRef(false) // one request, even when React runs the effect twice

  const start = (acceptTerms: boolean) => {
    if (!k || started.current) return
    started.current = true
    const body: RequestPreviewRequest = acceptTerms ? { key: k, acceptTerms: true } : { key: k }
    api<RequestPreviewResponse>(`/audits/${encodeURIComponent(audit.id)}/preview`, { method: 'POST', json: body })
      .then(() => router.invalidate({ filter: (m) => m.routeId === Route.id }))
      .catch((err) => {
        started.current = false
        setError(errorMessage(err))
      })
  }
  const auto = !!k && audit.hasEmail && !audit.needsTerms && !audit.previewToken
  useEffect(() => {
    if (auto) start(false)
  }, [auto]) // eslint-disable-line react-hooks/exhaustive-deps

  const heading = (
    <div className="flex flex-col gap-1">
      <h3 className="text-xl font-semibold">Your free homepage preview</h3>
      <p className="text-muted-foreground">See your homepage on the new platform before you pay.</p>
    </div>
  )
  if (audit.previewToken) return <PreviewStatus token={audit.previewToken} heading={heading} onPreview={onPreview} />
  if (!k || !audit.hasEmail) {
    return (
      <div className="flex flex-col gap-2">
        {heading}
        <p className="text-sm text-muted-foreground">
          {audit.orderId ? 'A preview was already started for this audit. Open it from the link in your email.' : 'The preview starts when you open this report from the link in your email.'}
        </p>
      </div>
    )
  }
  if (audit.needsTerms) {
    return (
      <div className="flex flex-col gap-4">
        {heading}
        <label className="flex items-start gap-3 text-sm">
          <Checkbox checked={accepted} onCheckedChange={(c) => setAccepted(c === true)} className="mt-0.5" />
          <span>
            I accept the{' '}
            <a href="/terms" target="_blank" rel="noopener" className="underline underline-offset-4">
              terms of service
            </a>{' '}
            and the{' '}
            <a href="/privacy" target="_blank" rel="noopener" className="underline underline-offset-4">
              privacy policy
            </a>
            , and I own this website or act for its owner.
          </span>
        </label>
        <FormError message={error} />
        <Button size="lg" className="h-11 w-fit px-6 text-base" disabled={!accepted} onClick={() => start(true)}>
          Build my free preview
        </Button>
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-3">
      {heading}
      {error ? (
        <FormError message={error} />
      ) : (
        <p role="status" className="flex items-center gap-2 text-muted-foreground">
          <Spinner aria-hidden /> Starting your preview
        </p>
      )}
    </div>
  )
}

/** The homepage preview of this audit: building, or ready next to the speed gain. */
function PreviewStatus({ token, heading, onPreview }: { token: string; heading: React.ReactNode; onPreview: (p: PreviewView) => void }) {
  const [preview, setPreview] = useState<PreviewView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = () =>
    api<PreviewView>(`/previews/${encodeURIComponent(token)}`)
      .then((p) => {
        setPreview(p)
        onPreview(p)
      })
      .catch((err) => setError(errorMessage(err)))
  useEffect(() => void load(), [token]) // eslint-disable-line react-hooks/exhaustive-deps
  // Our team may finish a closer look at any time, so that state keeps refreshing too, more slowly.
  useInterval(() => void load(), !preview || preview.state === 'preview_requested' ? 15000 : preview.state === 'needs_human' && !preview.previewUrl ? 60000 : null)

  if (error && !preview) return <FormError message={error} />
  if (!preview) return <Spinner aria-label="Loading your preview" />
  if (preview.state === 'needs_human' && !preview.previewUrl) {
    return (
      <div className="flex flex-col gap-3">
        {heading}
        <div role="status" className="flex flex-col gap-1 rounded-xl border border-border p-5">
          <p className="font-medium">Our team is taking a closer look at your site</p>
          <p className="text-sm text-muted-foreground">Our automatic rebuild could not finish on its own. We email you, usually within 48 hours, and your preview appears here by itself.</p>
        </div>
      </div>
    )
  }
  if (preview.state === 'preview_requested') {
    return (
      <div className="flex flex-col gap-3">
        {heading}
        <div role="status" className="flex items-start gap-3 rounded-xl border border-dashed border-border p-5">
          <Spinner className="mt-0.5 size-5" aria-hidden />
          <div className="flex flex-col gap-1">
            <p className="font-medium">We are rebuilding your homepage now</p>
            <p className="text-sm text-muted-foreground">It usually takes under 30 minutes. We email you when it is ready, and it appears here by itself.</p>
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr] lg:items-start">
      <ScreenFrame src={preview.rebuilt.desktop} alt="Your rebuilt homepage, desktop view" viewport="desktop" heightClass="h-96" />
      <div className="flex flex-col gap-4">
        {heading}
        {preview.speed.original != null && preview.speed.rebuilt != null && (
          <p className="text-lg">
            Mobile speed <span className="font-semibold tabular-nums">{preview.speed.original}</span> →{' '}
            <span className={cn('font-semibold tabular-nums', preview.speed.rebuilt >= preview.speed.original && 'text-success')}>{preview.speed.rebuilt}</span> of 100
          </p>
        )}
        <Link to="/preview/$token" params={{ token }} className={cn(buttonVariants({ variant: 'outline', size: 'lg' }), 'w-fit')}>
          Compare it with your site
        </Link>
      </div>
    </div>
  )
}
